import React, { useEffect, useRef, useState } from "react";

// Reusable live camera barcode scanner.
// Uses the browser's native BarcodeDetector API (supported on Chrome/Android — covers the
// vast majority of Indian distributor devices) with no extra npm dependency required.
// Falls back to a manual-entry box if the API isn't available (e.g. some iPhones/Safari).
export default function BarcodeScannerModal({ onScan, onClose }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const [supported, setSupported] = useState(true);
  const [err, setErr] = useState("");
  const [manual, setManual] = useState("");

  useEffect(() => {
    if (!("BarcodeDetector" in window)) {
      setSupported(false);
      return;
    }
    let stopped = false;
    const detector = new window.BarcodeDetector({
      formats: ["ean_13", "ean_8", "upc_a", "upc_e", "code_128", "code_39", "qr_code"],
    });

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
        });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        const tick = async () => {
          if (stopped) return;
          try {
            const codes = await detector.detect(videoRef.current);
            if (codes && codes.length > 0) {
              const value = codes[0].rawValue;
              stopped = true;
              cleanup();
              onScan(value);
              return;
            }
          } catch {}
          rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
      } catch (e) {
        setErr("Camera access denied or unavailable. Type the barcode manually below.");
        setSupported(false);
      }
    })();

    function cleanup() {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    }
    return cleanup;
  }, []);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 9999, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ width: "100%", maxWidth: 420, background: "#fff", borderRadius: 14, overflow: "hidden" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", background: "#1C2B3A" }}>
          <div style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>📷 Scan Barcode</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#fff", fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>

        {supported ? (
          <div style={{ position: "relative", background: "#000" }}>
            <video ref={videoRef} style={{ width: "100%", display: "block" }} muted playsInline />
            <div style={{ position: "absolute", inset: 0, border: "3px solid #22C55E", margin: 40, borderRadius: 10, pointerEvents: "none" }} />
          </div>
        ) : (
          <div style={{ padding: 20 }}>
            {err && <div style={{ color: "#DC2626", fontSize: 12, marginBottom: 10 }}>⚠️ {err}</div>}
            {!err && <div style={{ color: "#92400E", fontSize: 12, marginBottom: 10 }}>Live camera scanning isn't supported on this device/browser. Type the barcode manually:</div>}
            <input
              autoFocus
              value={manual}
              onChange={(e) => setManual(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && manual.trim()) onScan(manual.trim()); }}
              placeholder="Enter barcode number..."
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #D1D5DB", fontSize: 14, boxSizing: "border-box", marginBottom: 10 }}
            />
            <button
              onClick={() => manual.trim() && onScan(manual.trim())}
              style={{ width: "100%", padding: "10px", borderRadius: 8, border: "none", background: "#2563EB", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" }}
            >
              Use This Barcode
            </button>
          </div>
        )}

        <div style={{ padding: "10px 16px", fontSize: 11, color: "#6B7280", textAlign: "center" }}>
          Point the camera at the barcode — it will scan automatically.
        </div>
      </div>
    </div>
  );
}
